import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

/**
 * Custom hook to fetch checklists page data using optimized RPC
 * Includes search debouncing, filtering, and pagination
 *
 * @param {string} companyId - The company ID
 * @param {string} userId - The current user's ID
 * @param {object} options - Configuration options
 * @param {string} options.projectId - Project ID (for project-level pages)
 * @param {boolean} options.isProjectLevel - Whether this is a project-level page
 * @param {string} options.search - Search query
 * @param {boolean} options.showCompleted - Whether to show completed checklists
 * @param {boolean} options.showAssignedToMe - Filter to user's assignments
 * @param {number} options.page - Current page
 * @param {number} options.limit - Items per page
 * @param {boolean} options.enabled - Whether to fetch data
 * @returns {object} { checklists, templates, space, summary, pagination, loading, error, refetch, updateChecklistLocally, removeChecklistLocally }
 */
export const useChecklistsData = (companyId, userId, options = {}) => {
  const {
    projectId = null,
    isProjectLevel = false,
    search = '',
    showCompleted = false,
    showAssignedToMe = false,
    page = 1,
    limit = 50,
    enabled = true
  } = options;

  const [checklists, setChecklists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [space, setSpace] = useState(null);
  const [summary, setSummary] = useState({
    total: 0,
    inProgress: 0,
    completed: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    totalCount: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const debounceTimerRef = useRef(null);

  // Debounce search input (300ms)
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search]);

  const fetchData = useCallback(async () => {
    if (!companyId || !enabled) {
      setLoading(false);
      return;
    }

    // For project-level, we need projectId
    if (isProjectLevel && !projectId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase.rpc('get_checklists_page_data', {
        p_company_id: companyId,
        p_user_id: userId || null,
        p_project_id: projectId,
        p_is_project_level: isProjectLevel,
        p_show_completed: showCompleted,
        p_show_assigned_to_me: showAssignedToMe,
        p_search: debouncedSearch || null,
        p_page: page,
        p_limit: limit
      });

      if (fetchError) throw fetchError;

      setChecklists(data.checklists || []);
      setTemplates(data.templates || []);
      setSpace(data.space || null);
      setSummary(data.summary || { total: 0, inProgress: 0, completed: 0 });
      setPagination(data.pagination || { page: 1, limit: 50, totalCount: 0, totalPages: 0 });
    } catch (err) {
      console.error('Error fetching checklists data:', err);
      setError(err);
      toast.error('Failed to load checklists');
    } finally {
      setLoading(false);
    }
  }, [companyId, userId, projectId, isProjectLevel, showCompleted, showAssignedToMe, debouncedSearch, page, limit, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Update a checklist in local state
   * @param {string} checklistId - The checklist ID
   * @param {object} updates - The fields to update
   */
  const updateChecklistLocally = useCallback((checklistId, updates) => {
    setChecklists(prev => prev.map(checklist =>
      checklist.id === checklistId ? { ...checklist, ...updates } : checklist
    ));
  }, []);

  /**
   * Remove a checklist from local state
   * @param {string} checklistId - The checklist ID
   */
  const removeChecklistLocally = useCallback((checklistId) => {
    setChecklists(prev => prev.filter(checklist => checklist.id !== checklistId));
  }, []);

  /**
   * Add a checklist to local state
   * @param {object} checklist - The checklist to add
   */
  const addChecklistLocally = useCallback((checklist) => {
    setChecklists(prev => [checklist, ...prev]);
  }, []);

  return {
    checklists,
    templates,
    space,
    summary,
    pagination,
    loading,
    error,
    refetch: fetchData,
    updateChecklistLocally,
    removeChecklistLocally,
    addChecklistLocally
  };
};
